import type { ThanksConfig } from '@coorum-research/shared-common/types'
import type { ReactNode } from 'react'
import { use, useMemo } from 'react'
import taskCompleteImage from '../media/taskComplete.png'
import thumbsUpImage from '../media/thumbsUp.png'
import trophyImage from '../media/trophy.png'
import { getPublicCloudflareVideoThumbnailUrl } from '../utils'
import { MediaItem } from './MediaItem'
import { ThemeButton } from './ThemeButton'
import { defaultTheme, ThemeContext } from './ThemeContext'
import { WidgetMarkdown } from './WidgetMarkdown'

export const defaultContent: Props['content'] = {
  type: 'thanks',
  headingContent: 'ご回答ありがとうございます',
}

/**
 * FIXME: image path悩ましい。
 */
export function getPresetImage(presetImage?: string, imageUrl?: string): string | undefined {
  let imageHost = ''
  if (import.meta.env.PROD && import.meta.env.VITE_WIDGET_CDN_HOST) {
    imageHost = import.meta.env.VITE_WIDGET_CDN_HOST
  }

  switch (presetImage) {
  case 'trophy':
    // return trophyImage
    return `${imageHost}${trophyImage}`
  case 'thumbsUp':
    // return thumbsUpImage
    return `${imageHost}${thumbsUpImage}`
  case 'taskComplete':
    // return taskCompleteImage
    return `${imageHost}${taskCompleteImage}`
  case 'original':
    // return original
    return imageUrl
  default:
    return undefined
  }
}

type ThankLinkType = 'paragraphContent' | 'image'

type Props = {
  content?: ThanksConfig
  onClickThanksLink?: (thanksLinkType: ThankLinkType, thanksLinkUrl: string | undefined) => void
  onClick?: () => void
  isMypageVisible?: boolean
}

function Layout({ children }: {
  children: ReactNode
}) {
  const { mainTextColor } = use(ThemeContext)

  return (
    <div className="flex size-full flex-col gap-8 px-6 py-10" style={{ color: mainTextColor }}>
      {children}
    </div>
  )
}

export function ThanksComponent({
  content,
  onClickThanksLink,
  onClick,
  isMypageVisible = true,
}: Props) {
  const {
    theme,
    headingContent,
    presetImage,
    paragraphContent,
    footerButtonContent,
    imageUrl,
    mediaFileType,
    videoUrl,
    videoUid,
  } = {
    ...defaultContent,
    ...content,
  }

  const presetOrUrlImage = getPresetImage(presetImage, imageUrl)

  const handleClick = (thanksLinkType: ThankLinkType, thanksLinkUrl: string | undefined) => {
    if (onClickThanksLink) {
      onClickThanksLink(thanksLinkType, thanksLinkUrl)
    }
  }

  /**
   * presetImageがoriginalの場合だけ、videoが存在する
   * なので、presetImageがoriginalではない場合には、強制的にmediaFileTypeをimageにする
   */
  const computedMediaFileType = useMemo(() => {
    if (presetImage === 'original') {
      return mediaFileType
    }
    return 'image'
  }, [presetImage, mediaFileType])

  const isMediaExist = useMemo(() => {
    return presetOrUrlImage || videoUrl
  }, [presetOrUrlImage, videoUrl])

  return (
    <ThemeContext value={{ ...defaultTheme, ...theme }}>
      <Layout>
        <div className="flex grow flex-col items-center justify-center">
          {headingContent && (
            <h1 className="whitespace-pre-wrap text-center text-[24px] font-bold">
              {headingContent}
            </h1>
          )}
          {isMediaExist && (
            content?.imageLinkUrl
              ? (
                <button
                  type="button"
                  onClick={() => handleClick('image', content.imageLinkUrl)}
                >
                  <MediaItem
                    mediaFileType={computedMediaFileType}
                    videoUrl={videoUrl}
                    videoThumbnailUrl={getPublicCloudflareVideoThumbnailUrl(videoUid)}
                    imageUrl={presetOrUrlImage}
                    alt="thanks-image"
                    className="mt-4 h-72 object-contain"
                  />
                </button>
              )
              : (
                <MediaItem
                  mediaFileType={computedMediaFileType}
                  videoUrl={videoUrl}
                  videoThumbnailUrl={getPublicCloudflareVideoThumbnailUrl(videoUid)}
                  imageUrl={presetOrUrlImage}
                  alt="thanks-image"
                  className="mt-4 h-72 object-contain"
                />
              )
          )}
          {paragraphContent && (
            <WidgetMarkdown
              className="mt-2 whitespace-pre-wrap text-center text-[24px] font-bold"
              content={paragraphContent}
              components={{
                a: ({ ...props }) => (
                  <button
                    type="button"
                    className="text-widget-link hover:underline"
                    onClick={() => handleClick('paragraphContent', props.href)}
                  >
                    {props.children}
                  </button>
                ),
              }}
            />
          )}
        </div>
        {isMypageVisible && onClick && (
          <div className="mt-auto">
            <ThemeButton onClick={onClick} className="mb-4">
              {footerButtonContent ?? '体験記録を見る'}
            </ThemeButton>
          </div>
        )}
      </Layout>
    </ThemeContext>
  )
}
